import type { ReactNode } from 'react';
import styles from './field.module.css';

/**
 * Field - the label + control + hint/error wrapper, so every form row shares
 * one arrangement and one typography instead of re-rolling `.label`/`.hint`
 * classes per surface. Purely structural: the control child keeps its own
 * chrome (and its own aria-label when the visual label is not associable).
 *
 *   - `layout` `'stack'` (label above, the form default) or `'row'` (label
 *     beside, for a single compact control like a mode picker).
 *   - `error` replaces `hint` when present and announces itself.
 *   - `htmlFor` renders the label as a real `<label>` when the control is a
 *     labellable element (an Input/Textarea with an `id`).
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  layout = 'stack',
  className,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode | null;
  layout?: 'stack' | 'row';
  /** Layout hook only (grid placement, width); chrome stays here. */
  className?: string;
  children: ReactNode;
}) {
  const LabelTag = htmlFor ? 'label' : 'span';
  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')} data-layout={layout}>
      <div className={styles.top}>
        <LabelTag className={styles.label} {...(htmlFor ? { htmlFor } : {})}>
          {label}
        </LabelTag>
        <div className={styles.control}>{children}</div>
      </div>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
}
